/** Factory reference: 10 sites × 3 modules/site */
export const FACTORY_MODULES = 30;
export const LEARNING_RATE_PER_DOUBLING = 0.15;
export const MAX_FACTORY_SCALE_MULTIPLIER = 1.5;

/**
 * Wright's law exponent: 15% cost reduction per doubling => b = -log2(0.85)
 */
export function getWrightExponent(
  learningRatePerDoubling = LEARNING_RATE_PER_DOUBLING,
) {
  return -Math.log2(1 - learningRatePerDoubling);
}

function resolveFactoryModules(options = {}) {
  if (options.factoryModules != null) {
    return options.factoryModules;
  }
  if (options.factorySites != null) {
    const modulesPerSite = options.modulesPerFactorySite ?? 3;
    return options.factorySites * modulesPerSite;
  }
  return FACTORY_MODULES;
}

function resolveMaxMultiplier(options = {}) {
  return options.maxMultiplier ?? MAX_FACTORY_SCALE_MULTIPLIER;
}

/**
 * Uncapped Wright's law multiplier from module count.
 */
export function getRawFactoryScaleMultiplier(numModules, options = {}) {
  const factoryModules = resolveFactoryModules(options);
  const learningRate =
    options.learningRatePerDoubling ?? LEARNING_RATE_PER_DOUBLING;
  const modules = Math.max(1, Math.floor(numModules) || 1);

  if (modules >= factoryModules) {
    return 1;
  }

  const b = getWrightExponent(learningRate);
  return Math.pow(factoryModules / modules, b);
}

/**
 * Multiplier on linear totals, capped at maxMultiplier (default 1.5).
 * @param {number} numModules - Total modules (sites × modules per site)
 */
export function getFactoryScaleMultiplier(numModules, options = {}) {
  const raw = getRawFactoryScaleMultiplier(numModules, options);
  const maxMult = resolveMaxMultiplier(options);
  return Math.min(raw, maxMult);
}

/**
 * @param {object} results - calculateCosts output (mutated)
 * @param {object} opts
 * @param {number} opts.numModules
 * @param {number} opts.profitMargin
 * @param {object} [opts.factoryScale]
 */
export function applyFactoryScaleToCostResults(results, opts) {
  const { numModules, profitMargin, factoryScale = {} } = opts;
  const factoryModules = resolveFactoryModules(factoryScale);
  const rawScaleMultiplier = getRawFactoryScaleMultiplier(
    numModules,
    factoryScale,
  );
  const scaleMultiplier = getFactoryScaleMultiplier(numModules, factoryScale);

  results.linearOneTimeCost = results.oneTimeCost;
  results.linearRecurringCost = results.recurringCost;
  results.linearTotalCost = results.oneTimeCost + results.recurringCost;
  results.rawScaleMultiplier = rawScaleMultiplier;
  results.scaleMultiplier = scaleMultiplier;
  results.factoryModules = factoryModules;
  results.scaleMultiplierCapped =
    rawScaleMultiplier > scaleMultiplier + 1e-9;

  if (scaleMultiplier === 1) {
    results.totalCost = results.linearTotalCost;
    results.totalProfit = results.totalCost * (profitMargin - 1);
    results.totalPrice = results.totalCost * profitMargin;
    results.oneTimeTotal = results.oneTimeCost;
    results.recurringTotal = results.recurringCost;
    results.grandTotal = results.totalPrice;
    return results;
  }

  results.oneTimeCost = results.linearOneTimeCost * scaleMultiplier;
  results.recurringCost = results.linearRecurringCost * scaleMultiplier;
  results.items = results.items.map((item) => {
    if (item.skipped) {
      return item;
    }
    return {
      ...item,
      cost: item.cost * scaleMultiplier,
      price: item.price * scaleMultiplier,
    };
  });

  results.totalCost = results.oneTimeCost + results.recurringCost;
  results.totalProfit = results.totalCost * (profitMargin - 1);
  results.totalPrice = results.totalCost * profitMargin;
  results.oneTimeTotal = results.oneTimeCost;
  results.recurringTotal = results.recurringCost;
  results.grandTotal = results.totalPrice;

  return results;
}
